/* ============================================
   Portal — Checkout de Hosting (in-portal, Fase A)
   Compra completa dentro del portal reusando el backend /api/hosting/checkout.
   El cliente ya está logueado → prellenamos nombre/email. El pago se finaliza en
   MercadoPago (página segura) y vuelve al portal (origen: 'portal').
   ============================================ */

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import { Breadcrumbs } from '@/components/ui'

// Precios de referencia (PEN). El total REAL lo calcula el servidor en /checkout
// (incluye promo, dominio y cupón). Mantener en sync con PLANS del backend.
const PLANS = [
  { id: 'starter', name: 'Starter', desc: 'Emprendedores y sitios personales', monthly: 19, annual: 190 },
  { id: 'business', name: 'Business', desc: 'Negocios en crecimiento', monthly: 39, annual: 390, popular: true },
  { id: 'pro', name: 'Pro', desc: 'Proyectos de alto tráfico', monthly: 69, annual: 690 },
  { id: 'enterprise', name: 'Enterprise', desc: 'Máximo rendimiento', monthly: 149, annual: 1490 },
]

type DomResult = { dominio: string; tld: string; disponible: boolean; precio: number | null; requiereAsistencia?: boolean; motivoAsistencia?: string }

export default function PortalCheckoutHosting() {
  const { user } = useAuth()
  const api = useApi()
  const [sp] = useSearchParams()

  const email = String(user?.email || '')

  const [nombre, setNombre] = useState('')
  const [planId, setPlanId] = useState('business')
  const [periodo, setPeriodo] = useState<'mensual' | 'anual'>('anual')
  const [dominioTipo, setDominioTipo] = useState<'sin_dominio' | 'existente' | 'nuevo'>(sp.get('dominio') === 'nuevo' ? 'nuevo' : 'sin_dominio')
  const [dominioExistente, setDominioExistente] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [resultados, setResultados] = useState<DomResult[]>([])
  const [dominioNuevo, setDominioNuevo] = useState<DomResult | null>(null)
  const [addr, setAddr] = useState({ addressLine1: '', city: '', state: '', zipcode: '', country: 'PE', phoneCc: '51' })
  const [whatsapp, setWhatsapp] = useState('')
  const [ruc, setRuc] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [cupon, setCupon] = useState('')
  const [cuponInfo, setCuponInfo] = useState<{ valido: boolean; descuento_pct?: number; motivo?: string } | null>(null)
  const [validandoCupon, setValidandoCupon] = useState(false)
  const [metodo, setMetodo] = useState<'mercadopago' | 'transferencia'>('mercadopago')
  const [loading, setLoading] = useState(false)
  const [transfer, setTransfer] = useState<{ codigo: string; public_token: string; datos_bancarios: any } | null>(null)

  // Prellenar desde la ficha del cliente (nombre, teléfono y facturación). Sin
  // bloquear la edición: son valores por defecto que el cliente puede cambiar.
  useEffect(() => {
    api.get<{ nombre?: string; telefono?: string; facturacion?: { ruc_dni?: string; razon_social?: string } }>('/api/portal/perfil')
      .then(p => {
        if (p?.nombre) setNombre(prev => prev || p.nombre!)
        if (p?.telefono) setWhatsapp(prev => prev || p.telefono!)
        if (p?.facturacion?.ruc_dni) setRuc(prev => prev || p.facturacion!.ruc_dni!)
        if (p?.facturacion?.razon_social) setRazonSocial(prev => prev || p.facturacion!.razon_social!)
      })
      .catch(() => { /* perfil opcional: si falla, el cliente llena a mano */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Validar el cupón en vivo (debounced) contra el servidor → reflejar el
  // descuento en el resumen. El total real lo recalcula /checkout igual.
  useEffect(() => {
    const code = cupon.trim()
    if (!code) { setCuponInfo(null); setValidandoCupon(false); return }
    let cancel = false
    setValidandoCupon(true)
    const t = setTimeout(async () => {
      try {
        const r = await api.get<{ valido: boolean; descuento_pct?: number; motivo?: string }>(`/api/hosting/cupon/${encodeURIComponent(code)}`)
        if (!cancel) setCuponInfo(r)
      } catch {
        if (!cancel) setCuponInfo(null)
      } finally {
        if (!cancel) setValidandoCupon(false)
      }
    }, 450)
    return () => { cancel = true; clearTimeout(t) }
  }, [cupon])

  const plan = PLANS.find(p => p.id === planId)!
  const precioBase = periodo === 'anual' ? plan.annual : plan.monthly
  const CUPON_PISO = 5 // piso de MercadoPago (espejo de CUPON_PISO_PEN en el backend)
  const descuentoPct = cuponInfo?.valido ? (cuponInfo.descuento_pct || 0) : 0
  const totalEstimado = descuentoPct > 0
    ? Math.max(Math.round(precioBase * (1 - descuentoPct / 100) * 100) / 100, CUPON_PISO)
    : precioBase

  async function buscarDominio() {
    const name = busqueda.trim().toLowerCase().replace(/[^a-z0-9-]/g, '')
    if (!name) return
    setBuscando(true); setResultados([]); setDominioNuevo(null)
    try {
      const r = await api.get<{ results: DomResult[] }>(`/api/hosting/domains/search?name=${encodeURIComponent(name)}&tlds=com,net,org,online,store`)
      setResultados(r?.results || [])
    } catch { showToast('No se pudo buscar el dominio', 'error') }
    finally { setBuscando(false) }
  }

  async function pagar() {
    if (!nombre.trim()) { showToast('Ingresa tu nombre', 'error'); return }
    if (!whatsapp.trim()) { showToast('Ingresa tu WhatsApp de contacto', 'error'); return }
    if (dominioTipo === 'existente' && !dominioExistente.trim()) { showToast('Ingresa tu dominio', 'error'); return }
    if (dominioTipo === 'nuevo') {
      if (!dominioNuevo) { showToast('Busca y elige un dominio para registrar', 'error'); return }
      if (!addr.addressLine1 || !addr.city || !addr.zipcode) { showToast('Completa la dirección del titular del dominio', 'error'); return }
    }
    setLoading(true)
    try {
      const body: Record<string, unknown> = {
        nombre, email, whatsapp: whatsapp.trim(),
        ruc: ruc.trim() || undefined,
        razon_social: (ruc.trim() && razonSocial.trim()) ? razonSocial.trim() : undefined,
        plan_id: planId, periodo, moneda: 'PEN',
        metodo_pago: metodo,
        dominio_tipo: dominioTipo,
        dominio: dominioTipo === 'existente' ? dominioExistente.trim() : dominioTipo === 'nuevo' ? dominioNuevo?.dominio : undefined,
        cupon: cupon.trim() || undefined,
        origen: 'portal',
        ...(dominioTipo === 'nuevo' ? addr : {}),
      }
      const r = await api.post<{ codigo: string; public_token: string; checkout_url?: string; metodo?: string; datos_bancarios?: any }>('/api/hosting/checkout', body)
      if (r?.checkout_url) {
        window.location.href = r.checkout_url // → MercadoPago, vuelve al portal
      } else if (r?.metodo === 'transferencia') {
        setTransfer({ codigo: r.codigo, public_token: r.public_token, datos_bancarios: r.datos_bancarios })
      } else {
        showToast('No se pudo iniciar el pago', 'error')
      }
    } catch (err: any) {
      showToast(err.message || 'Error al procesar el pedido', 'error')
    } finally { setLoading(false) }
  }

  // ── Vista de transferencia (tras registrar el pedido) ──
  if (transfer) {
    const d = transfer.datos_bancarios || {}
    return (
      <div className="portal-page-enter" style={{ padding: '2rem', maxWidth: 560 }}>
        <span className="portal-saludo">¡Pedido registrado!</span>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>Tu código es <strong>{transfer.codigo}</strong>. Transfiere a esta cuenta y envíanos el comprobante; activamos tu hosting en 1 a 24 h.</p>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
          {[['Banco', d.banco], ['Cuenta', d.cuenta], ['CCI', d.cci], ['Titular', d.titular], ['RUC', d.ruc_empresa]].map(([k, v]) => v ? (
            <div key={k as string} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.9rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>{k}</span><span style={{ fontFamily: 'monospace' }}>{v}</span>
            </div>
          ) : null)}
        </div>
        <a href={`https://www.1conecta.com/infraestructura/hosting/comprobante?ref=${transfer.public_token}`} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ marginTop: 20 }}>Subir mi comprobante</a>
      </div>
    )
  }

  const dominioLabel = dominioTipo === 'existente'
    ? (dominioExistente.trim() || 'Tu dominio actual')
    : dominioTipo === 'nuevo'
      ? (dominioNuevo?.dominio || 'Por elegir')
      : 'Sin dominio (después)'

  return (
    <div className="portal-page-enter pch-wrap" style={{ padding: '2rem' }}>
      <Link to="/portal/nuevo-pedido" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textDecoration: 'none' }}>← Volver</Link>
      <Breadcrumbs items={[{ label: 'Portal', to: '/portal' }, { label: 'Servicios', to: '/portal/nuevo-pedido' }, { label: 'Hosting' }]} />

      <div className="pch-grid">
        {/* ── Columna izquierda: pasos ── */}
        <div className="pch-main">
          {/* Paso 1 — Plan */}
          <h3 className="pch-step-title">1. Elige tu plan</h3>
          <div style={{ display: 'inline-flex', gap: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 30, padding: 4, marginBottom: 12 }}>
            {(['mensual', 'anual'] as const).map(p => (
              <button key={p} onClick={() => setPeriodo(p)} className={`btn btn-sm ${periodo === p ? 'btn-primary' : 'btn-ghost'}`} style={{ borderRadius: 30 }}>
                {p === 'anual' ? 'Anual (2 meses gratis)' : 'Mensual'}
              </button>
            ))}
          </div>
          <div className="pch-plans">
            {PLANS.map(p => {
              const precio = periodo === 'anual' ? p.annual : p.monthly
              const sel = planId === p.id
              return (
                <button key={p.id} onClick={() => setPlanId(p.id)} style={{
                  textAlign: 'left', cursor: 'pointer', background: 'var(--bg-card)', color: 'var(--text-primary)',
                  border: `1.5px solid ${sel ? 'var(--info)' : 'var(--border)'}`, borderRadius: 14, padding: '14px 16px', position: 'relative',
                }}>
                  {p.popular && <span style={{ position: 'absolute', top: -8, right: 12, fontSize: '0.6rem', fontWeight: 700, background: 'var(--info)', color: 'var(--on-info)', padding: '2px 8px', borderRadius: 20 }}>POPULAR</span>}
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', minHeight: 32, marginTop: 2 }}>{p.desc}</div>
                  <div style={{ marginTop: 8, fontWeight: 700, fontSize: '1.15rem' }}>S/{precio}<span style={{ fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-secondary)' }}>/{periodo === 'anual' ? 'año' : 'mes'}</span></div>
                </button>
              )
            })}
          </div>

          {/* Paso 2 — Dominio */}
          <h3 className="pch-step-title">2. Dominio</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {([['sin_dominio', 'Después / sin dominio'], ['existente', 'Ya tengo dominio'], ['nuevo', 'Registrar uno nuevo']] as const).map(([v, label]) => (
              <button key={v} onClick={() => setDominioTipo(v)} className={`btn btn-sm ${dominioTipo === v ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
            ))}
          </div>
          {dominioTipo === 'existente' && (
            <input className="form-input" placeholder="midominio.com" value={dominioExistente} onChange={e => setDominioExistente(e.target.value)} style={{ maxWidth: 340 }} />
          )}
          {dominioTipo === 'nuevo' && (
            <div>
              <div style={{ display: 'flex', gap: 8, maxWidth: 460 }}>
                <input className="form-input" placeholder="Busca tu dominio (sin .com)" value={busqueda} onChange={e => setBusqueda(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscarDominio()} />
                <button className="btn btn-primary" onClick={buscarDominio} disabled={buscando}>{buscando ? '...' : 'Buscar'}</button>
              </div>
              <div style={{ display: 'grid', gap: 6, marginTop: 10, maxWidth: 460 }}>
                {resultados.map(r => (
                  <button key={r.dominio} disabled={!r.disponible} onClick={() => setDominioNuevo(r)} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left', cursor: r.disponible ? 'pointer' : 'not-allowed',
                    color: 'var(--text-primary)',
                    background: dominioNuevo?.dominio === r.dominio ? 'color-mix(in srgb, var(--info) 10%, transparent)' : 'var(--bg-card)',
                    border: `1px solid ${dominioNuevo?.dominio === r.dominio ? 'var(--info)' : 'var(--border)'}`, borderRadius: 10, padding: '9px 14px', opacity: r.disponible ? 1 : 0.5,
                  }}>
                    <span style={{ fontWeight: 500 }}>{r.dominio}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{r.requiereAsistencia ? 'Requiere asistencia' : r.disponible ? (r.precio != null ? `S/${r.precio}/año` : '—') : 'No disponible'}</span>
                  </button>
                ))}
              </div>
              {dominioNuevo && (
                <div style={{ marginTop: 14 }}>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 8 }}>Datos del titular del dominio (requerido por el registrador):</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, maxWidth: 460 }}>
                    <input className="form-input" placeholder="Dirección" value={addr.addressLine1} onChange={e => setAddr({ ...addr, addressLine1: e.target.value })} />
                    <input className="form-input" placeholder="Ciudad" value={addr.city} onChange={e => setAddr({ ...addr, city: e.target.value })} />
                    <input className="form-input" placeholder="Código postal" value={addr.zipcode} onChange={e => setAddr({ ...addr, zipcode: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Paso 3 — Datos */}
          <h3 className="pch-step-title">3. Tus datos</h3>
          <div className="pch-fields">
            <div>
              <label className="form-label">Nombre</label>
              <input className="form-input" placeholder="Tu nombre y apellido" value={nombre} onChange={e => setNombre(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Correo</label>
              <input className="form-input" value={email} disabled style={{ opacity: 0.7 }} />
            </div>
            <div>
              <label className="form-label">WhatsApp *</label>
              <input className="form-input" placeholder="+51 999 999 999" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} />
            </div>
            <div>
              <label className="form-label">RUC (opcional, para factura)</label>
              <input className="form-input" placeholder="20XXXXXXXXX" value={ruc} onChange={e => setRuc(e.target.value)} />
            </div>
            {ruc.trim() && (
              <div className="pch-field-full">
                <label className="form-label">Razón social</label>
                <input className="form-input" value={razonSocial} onChange={e => setRazonSocial(e.target.value)} />
              </div>
            )}
            <div>
              <label className="form-label">Cupón (opcional)</label>
              <input className="form-input" value={cupon} onChange={e => setCupon(e.target.value)} style={{ textTransform: 'uppercase' }} />
              {cupon.trim() && (
                <div style={{ fontSize: '0.74rem', marginTop: 4 }}>
                  {validandoCupon
                    ? <span style={{ color: 'var(--text-secondary)' }}>Validando cupón…</span>
                    : cuponInfo?.valido
                      ? <span style={{ color: 'var(--success)' }}>✓ Cupón válido — {cuponInfo.descuento_pct}% de descuento</span>
                      : cuponInfo
                        ? <span style={{ color: 'var(--danger)' }}>✕ {cuponInfo.motivo || 'Cupón no válido'}</span>
                        : null}
                </div>
              )}
            </div>
          </div>

          {/* Paso 4 — Método de pago */}
          <h3 className="pch-step-title">4. Método de pago</h3>
          <div className="pch-methods">
            <button onClick={() => setMetodo('mercadopago')} className="pch-method" data-sel={metodo === 'mercadopago'}>
              <span className="pch-method-label">Tarjeta / Yape / Plin</span>
              <span className="pch-method-logos">
                <img src="/assets/pagos/visa.svg" alt="Visa" title="Visa" />
                <img src="/assets/pagos/mastercard.svg" alt="Mastercard" title="Mastercard" />
                <img src="/assets/pagos/amex.svg" alt="American Express" title="American Express" />
                <img src="/assets/pagos/diners.svg" alt="Diners Club" title="Diners Club" />
                <span className="pch-badge" style={{ background: '#742284' }}>Yape</span>
                <span className="pch-badge" style={{ background: '#EE7203', fontSize: '0.5rem' }}>pagoefectivo</span>
              </span>
            </button>
            <button onClick={() => setMetodo('transferencia')} className="pch-method" data-sel={metodo === 'transferencia'}>
              <span className="pch-method-label">Transferencia bancaria</span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>BCP · Interbank · BBVA</span>
            </button>
          </div>
        </div>

        {/* ── Columna derecha: resumen fijo ── */}
        <aside className="pch-summary">
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 12px' }}>Resumen del pedido</h3>
          <div className="pch-summary-row">
            <span style={{ color: 'var(--text-secondary)' }}>Plan</span>
            <span style={{ fontWeight: 600, textAlign: 'right' }}>{plan.name}</span>
          </div>
          <div className="pch-summary-row">
            <span style={{ color: 'var(--text-secondary)' }}>Periodo</span>
            <span style={{ fontWeight: 600 }}>{periodo === 'anual' ? 'Anual' : 'Mensual'}</span>
          </div>
          <div className="pch-summary-row">
            <span style={{ color: 'var(--text-secondary)' }}>Dominio</span>
            <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{dominioLabel}</span>
          </div>
          {cupon.trim() && (
            <div className="pch-summary-row">
              <span style={{ color: 'var(--text-secondary)' }}>Cupón</span>
              <span style={{ fontWeight: 600, textTransform: 'uppercase', color: descuentoPct > 0 ? 'var(--success)' : 'var(--text-secondary)', textAlign: 'right' }}>
                {cupon.trim()}{descuentoPct > 0 ? ` (−${descuentoPct}%)` : validandoCupon ? ' …' : ''}
              </span>
            </div>
          )}
          <div className="pch-summary-total">
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>Desde</span>
            <span style={{ fontWeight: 700, fontSize: '1.4rem', textAlign: 'right' }}>
              {descuentoPct > 0 && (
                <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)', textDecoration: 'line-through', marginRight: 6 }}>S/{precioBase}</span>
              )}
              S/{totalEstimado}<span style={{ fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-secondary)' }}>/{periodo === 'anual' ? 'año' : 'mes'}</span>
            </span>
          </div>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: '8px 0 0' }}>
            {descuentoPct > 0
              ? 'Precio con cupón aplicado. El total final (con dominio) se confirma en el pago.'
              : 'El total final (con dominio y cupón) se confirma en el pago.'}
          </p>
          <button className="btn btn-primary" onClick={pagar} disabled={loading} style={{ width: '100%', marginTop: 16 }}>
            {loading ? 'Procesando…' : metodo === 'mercadopago' ? 'Ir a pagar' : 'Registrar pedido'}
          </button>
          <p style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', textAlign: 'center', margin: '10px 0 0' }}>
            {metodo === 'mercadopago' ? 'Pago seguro vía MercadoPago' : 'Te daremos los datos bancarios'}
          </p>
        </aside>
      </div>
    </div>
  )
}
